/**
 * SchoolAnalytics - Analytics dashboard, Risk detection, Announcements, Email Settings
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';
import {
  BarChart3, Users, GraduationCap, AlertTriangle, TrendingUp, TrendingDown,
  Loader2, Plus, Send, Trash2, Mail, Bell, Shield, CheckCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api/school${path}`, { ...opts, headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json', ...opts.headers } });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Request failed'); }
  return res.json();
};

const RISK_COLORS = { critical: 'bg-red-100 text-red-700 border-red-200', high: 'bg-orange-100 text-orange-700 border-orange-200', medium: 'bg-amber-100 text-amber-700 border-amber-200', low: 'bg-yellow-100 text-yellow-700 border-yellow-200' };

// ============== ANALYTICS OVERVIEW ==============
export const SchoolAnalytics = ({ tenantId }) => {
  const [view, setView] = useState('overview');

  return (
    <div className="space-y-4" data-testid="school-analytics">
      <div className="flex items-center gap-2 border-b pb-3">
        {['overview', 'classes', 'attendance', 'at-risk'].map(v => (
          <Button key={v} variant={view === v ? 'default' : 'ghost'} size="sm" onClick={() => setView(v)}
            className="capitalize" data-testid={`analytics-tab-${v}`}>{v.replace('-', ' ')}</Button>
        ))}
      </div>
      {view === 'overview' && <AnalyticsOverview tenantId={tenantId} />}
      {view === 'classes' && <ClassPerformance tenantId={tenantId} />}
      {view === 'attendance' && <AttendanceTrends tenantId={tenantId} />}
      {view === 'at-risk' && <AtRiskStudents tenantId={tenantId} />}
    </div>
  );
};

const AnalyticsOverview = ({ tenantId }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['analytics-overview', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/analytics/overview`),
  });
  const { data: gradeData } = useQuery({
    queryKey: ['analytics-grades', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/analytics/grade-distribution`),
  });

  if (isLoading) return <Loader2 className="h-6 w-6 animate-spin mx-auto" />;
  if (!data) return null;

  const { enrollment: e, attendance: a, academics: ac } = data;
  const grades = gradeData?.grades || {};
  const gradeOrder = ['A+', 'A', 'B+', 'B', 'C', 'D', 'F'];
  const maxGradeCount = Math.max(...Object.values(grades), 1);

  return (
    <div className="space-y-6" data-testid="analytics-overview">
      <h2 className="text-lg font-semibold">School Overview</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <StatCard icon={Users} label="Students" value={e.total_students} color="text-indigo-500" />
        <StatCard icon={GraduationCap} label="Teachers" value={e.total_teachers} color="text-emerald-500" />
        <StatCard icon={BarChart3} label="Classes" value={e.total_classes} color="text-blue-500" />
        <StatCard icon={CheckCircle} label="Avg Attendance" value={`${a.avg_rate}%`} color="text-green-500" />
        <StatCard icon={TrendingUp} label="Avg Grade" value={`${ac.avg_exam_score}%`} color="text-amber-500" />
        <StatCard icon={Bell} label="Pending Admissions" value={e.pending_admissions} color="text-rose-500" />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base">Gender Distribution</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-end gap-4 h-32">
              {[
                { label: 'Male', count: e.gender.male, color: 'bg-blue-400' },
                { label: 'Female', count: e.gender.female, color: 'bg-pink-400' },
              ].map(g => {
                const maxCount = Math.max(e.gender.male, e.gender.female, 1);
                const height = (g.count / maxCount) * 100;
                return (
                  <div key={g.label} className="flex-1 flex flex-col items-center gap-1">
                    <span className="text-xs font-medium">{g.count}</span>
                    <div className={`w-full rounded-t ${g.color}`} style={{ height: `${Math.max(height, 4)}%` }} />
                    <span className="text-xs text-slate-500">{g.label}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Grade Distribution {gradeData?.exam_name && `(${gradeData.exam_name})`}</CardTitle></CardHeader>
          <CardContent>
            {Object.keys(grades).length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">No exam data</p>
            ) : (
              <div className="flex items-end gap-2 h-32">
                {gradeOrder.filter(g => grades[g] != null).map(g => {
                  const height = (grades[g] / maxGradeCount) * 100;
                  const barColor = g.startsWith('A') ? 'bg-green-400' : g.startsWith('B') ? 'bg-blue-400' : g === 'C' ? 'bg-amber-400' : g === 'D' ? 'bg-orange-400' : 'bg-red-400';
                  return (
                    <div key={g} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-xs font-medium">{grades[g]}</span>
                      <div className={`w-full rounded-t ${barColor}`} style={{ height: `${Math.max(height, 4)}%` }} />
                      <span className="text-xs text-slate-500 font-medium">{g}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <Card>
    <CardContent className="p-3 text-center">
      <Icon className={`h-5 w-5 mx-auto mb-1 ${color}`} />
      <p className="text-xl font-bold">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </CardContent>
  </Card>
);

// ============== CLASS PERFORMANCE ==============
const ClassPerformance = ({ tenantId }) => {
  const { data = [], isLoading } = useQuery({
    queryKey: ['analytics-class-perf', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/analytics/class-performance`),
  });

  if (isLoading) return <Loader2 className="h-6 w-6 animate-spin mx-auto" />;

  return (
    <div className="space-y-4" data-testid="analytics-classes">
      <h2 className="text-lg font-semibold">Class Performance</h2>
      {data.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-8 text-center text-slate-400">No class data</CardContent></Card>
      ) : (
        <Table>
          <TableHeader><TableRow>
            <TableHead>Class</TableHead>
            <TableHead>Grade</TableHead>
            <TableHead className="text-center">Students</TableHead>
            <TableHead className="text-center">Attendance</TableHead>
            <TableHead className="text-center">Avg Score</TableHead>
            <TableHead>Class Teacher</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {data.map(c => (
              <TableRow key={c.id}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell>{c.grade_level || '-'}</TableCell>
                <TableCell className="text-center">{c.student_count}</TableCell>
                <TableCell className="text-center">
                  <Badge className={c.attendance_rate >= 80 ? 'bg-green-100 text-green-700' : c.attendance_rate >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>
                    {c.attendance_rate}%
                  </Badge>
                </TableCell>
                <TableCell className="text-center">
                  <Badge className={c.avg_score >= 70 ? 'bg-green-100 text-green-700' : c.avg_score >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>
                    {c.avg_score}%
                  </Badge>
                </TableCell>
                <TableCell>{c.class_teacher || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

// ============== ATTENDANCE TRENDS ==============
const AttendanceTrends = ({ tenantId }) => {
  const { data = [], isLoading } = useQuery({
    queryKey: ['analytics-att-trends', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/analytics/attendance-trends?days=30`),
  });

  if (isLoading) return <Loader2 className="h-6 w-6 animate-spin mx-auto" />;

  const maxTotal = Math.max(...data.map(d => d.total), 1);

  return (
    <div className="space-y-4" data-testid="analytics-attendance">
      <h2 className="text-lg font-semibold">Attendance Trends (Last 30 Days)</h2>
      {data.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-8 text-center text-slate-400">No attendance data</CardContent></Card>
      ) : (
        <>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-end gap-1 h-40 overflow-x-auto">
                {data.map(d => {
                  const height = (d.total / maxTotal) * 100;
                  const presentPct = d.total > 0 ? (d.present / d.total) * 100 : 0;
                  return (
                    <div key={d.date} className="flex flex-col items-center gap-0.5 min-w-[18px] flex-1" title={`${d.date}: ${d.rate}%`}>
                      <span className="text-[9px] text-slate-400">{d.rate}%</span>
                      <div className="w-full rounded-t relative" style={{ height: `${Math.max(height, 2)}%` }}>
                        <div className="absolute bottom-0 w-full bg-green-400 rounded-t" style={{ height: `${presentPct}%` }} />
                        <div className="absolute top-0 w-full bg-red-300 rounded-t" style={{ height: `${100 - presentPct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                <span>{data[0]?.date}</span>
                <span>{data[data.length - 1]?.date}</span>
              </div>
            </CardContent>
          </Card>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Date</TableHead>
              <TableHead className="text-center">Present</TableHead>
              <TableHead className="text-center">Absent</TableHead>
              <TableHead className="text-center">Late</TableHead>
              <TableHead className="text-center">Rate</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.slice().reverse().slice(0, 15).map(d => (
                <TableRow key={d.date}>
                  <TableCell>{d.date}</TableCell>
                  <TableCell className="text-center text-green-600 font-medium">{d.present}</TableCell>
                  <TableCell className="text-center text-red-600 font-medium">{d.absent}</TableCell>
                  <TableCell className="text-center text-amber-600 font-medium">{d.late}</TableCell>
                  <TableCell className="text-center"><Badge className={d.rate >= 80 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>{d.rate}%</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </div>
  );
};

// ============== AT-RISK STUDENTS ==============
const AtRiskStudents = ({ tenantId }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['analytics-at-risk', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/analytics/at-risk`),
  });

  if (isLoading) return <Loader2 className="h-6 w-6 animate-spin mx-auto" />;

  const students = data?.students || [];

  return (
    <div className="space-y-4" data-testid="analytics-at-risk">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">At-Risk Students</h2>
        <Badge variant="outline" className="text-sm">{data?.total_at_risk || 0} flagged</Badge>
      </div>
      <p className="text-xs text-slate-500">
        Thresholds: Attendance &lt; {data?.thresholds?.attendance_min || 75}% | Grade &lt; {data?.thresholds?.grade_min || 50}%
      </p>
      {students.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-8 text-center text-green-500 font-medium">No at-risk students detected</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {students.map(s => (
            <Card key={s.student_id} className={`border ${RISK_COLORS[s.risk_level] || ''}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    <span className="font-semibold">{s.name}</span>
                    <span className="text-xs text-slate-500">{s.admission_number}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={RISK_COLORS[s.risk_level]}>{s.risk_level}</Badge>
                    <span className="text-sm font-mono font-bold">{s.risk_score}</span>
                  </div>
                </div>
                <div className="flex gap-4 text-sm">
                  <span>Class: {s.class_name || 'N/A'}</span>
                  <span>Attendance: {s.attendance_rate}%</span>
                  {s.avg_grade != null && <span>Avg Grade: {s.avg_grade}%</span>}
                </div>
                <div className="mt-2 space-y-0.5">
                  {s.risk_factors.map((f, i) => (
                    <p key={i} className="text-xs"><AlertTriangle className="h-3 w-3 inline mr-1" />{f}</p>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ============== ANNOUNCEMENTS MANAGEMENT ==============
export const SchoolAnnouncements = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', body: '', audience: 'school', class_ids: [], priority: 'normal', send_email: false, send_whatsapp: false });

  const { data: announcements = [], isLoading } = useQuery({
    queryKey: ['school-announcements', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/announcements`),
  });

  const { data: classes = [] } = useQuery({
    queryKey: ['school-classes-ann', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/classes`),
  });

  const createMutation = useMutation({
    mutationFn: (data) => api(`/tenants/${tenantId}/announcements`, { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: (result) => {
      const parts = ['Announcement posted'];
      if (result.email_result?.sent) parts.push(`${result.email_result.sent} emails`);
      if (result.whatsapp_result?.queued) parts.push(`${result.whatsapp_result.queued} WhatsApp queued`);
      toast.success(parts.join(' · '));
      queryClient.invalidateQueries({ queryKey: ['school-announcements'] });
      setShowForm(false);
      setForm({ title: '', body: '', audience: 'school', class_ids: [], priority: 'normal', send_email: false, send_whatsapp: false });
    },
    onError: (e) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api(`/tenants/${tenantId}/announcements/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Deleted'); queryClient.invalidateQueries({ queryKey: ['school-announcements'] }); },
  });

  return (
    <div className="space-y-4" data-testid="school-announcements">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Announcements</h2>
        <Button size="sm" onClick={() => setShowForm(true)} data-testid="create-announcement-btn"><Plus className="h-4 w-4 mr-1" /> New Announcement</Button>
      </div>

      {isLoading ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : announcements.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-8 text-center text-slate-400">No announcements yet</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {announcements.map(a => (
            <Card key={a.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold">{a.title}</h3>
                      <Badge variant="outline" className="text-xs capitalize">{a.audience}</Badge>
                      {a.priority === 'urgent' && <Badge className="bg-red-100 text-red-700 text-xs">Urgent</Badge>}
                      {a.priority === 'high' && <Badge className="bg-orange-100 text-orange-700 text-xs">High</Badge>}
                    </div>
                    <p className="text-sm text-slate-600 whitespace-pre-wrap">{a.body}</p>
                    <p className="text-xs text-slate-400 mt-2">By {a.author_name} ({a.author_role}) | {new Date(a.created_at).toLocaleString()}</p>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => deleteMutation.mutate(a.id)} data-testid={`delete-ann-${a.id}`}>
                    <Trash2 className="h-4 w-4 text-slate-400" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>New Announcement</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Title</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="ann-title-input" /></div>
            <div><Label>Message</Label><Textarea value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} rows={4} data-testid="ann-body-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Audience</Label>
                <Select value={form.audience} onValueChange={v => setForm({ ...form, audience: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="school">Entire School</SelectItem>
                    <SelectItem value="class">Specific Classes</SelectItem>
                    <SelectItem value="teachers">All Teachers</SelectItem>
                    <SelectItem value="students">All Students</SelectItem>
                    <SelectItem value="parents">All Parents</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.audience === 'class' && (
              <div>
                <Label>Select Classes</Label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {classes.map(c => (
                    <button key={c.id} onClick={() => setForm(f => ({
                      ...f, class_ids: f.class_ids.includes(c.id) ? f.class_ids.filter(x => x !== c.id) : [...f.class_ids, c.id]
                    }))} className={`px-2 py-1 rounded text-xs border ${form.class_ids.includes(c.id) ? 'bg-indigo-100 border-indigo-300 text-indigo-700' : 'bg-white border-slate-200 text-slate-600'}`}>
                      {c.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Switch checked={form.send_email} onCheckedChange={v => setForm({ ...form, send_email: v })} data-testid="ann-send-email" />
              <Label className="text-sm">Also send via email</Label>
              <Mail className="h-4 w-4 text-slate-400" />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.send_whatsapp} onCheckedChange={v => setForm({ ...form, send_whatsapp: v })} data-testid="ann-send-whatsapp" />
              <Label className="text-sm">Also send via WhatsApp (OpenWA)</Label>
              <span className="text-emerald-600 text-sm">●</span>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => createMutation.mutate(form)} disabled={!form.title || !form.body || createMutation.isPending} data-testid="submit-announcement-btn">
              {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
              Post Announcement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== EMAIL SETTINGS ==============
export const SchoolEmailSettings = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    sender_email: '', sender_name: '',
    smtp_host: '', smtp_port: 587, smtp_user: '', smtp_password: '',
    smtp_use_tls: true, smtp_enabled: false,
  });
  const [passwordSet, setPasswordSet] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['school-email-settings', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/email-settings`),
  });

  React.useEffect(() => {
    if (data) {
      setForm({
        sender_email: data.sender_email || '',
        sender_name: data.sender_name || '',
        smtp_host: data.smtp_host || '',
        smtp_port: data.smtp_port || 587,
        smtp_user: data.smtp_user || '',
        smtp_password: '',   // never received from server
        smtp_use_tls: data.smtp_use_tls !== false,
        smtp_enabled: !!data.smtp_enabled,
      });
      setPasswordSet(!!data.smtp_password_set);
      // Auto-open the SMTP card when the school is already using its own SMTP.
      if (data.smtp_enabled || data.smtp_host) setShowAdvanced(true);
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (d) => api(`/tenants/${tenantId}/email-settings`, { method: 'PUT', body: JSON.stringify(d) }),
    onSuccess: () => {
      toast.success('Email settings saved');
      queryClient.invalidateQueries({ queryKey: ['school-email-settings'] });
    },
    onError: (e) => toast.error(e.message),
  });

  const testMutation = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/email-settings/test`, { method: 'POST' }),
    onSuccess: (r) => {
      if (r.sent > 0) {
        toast.success(`Test email sent via ${r.smtp_source || 'platform'} SMTP`);
      } else {
        toast.error(`Test failed: ${r.error || 'unknown'}`);
      }
    },
    onError: (e) => toast.error(e.message),
  });

  // Gmail / Outlook / Yahoo presets — populate host+port+tls with one click
  const applyPreset = (provider) => {
    const presets = {
      gmail:   { smtp_host: 'smtp.gmail.com',         smtp_port: 587, smtp_use_tls: true },
      outlook: { smtp_host: 'smtp-mail.outlook.com',  smtp_port: 587, smtp_use_tls: true },
      yahoo:   { smtp_host: 'smtp.mail.yahoo.com',    smtp_port: 587, smtp_use_tls: true },
      zoho:    { smtp_host: 'smtp.zoho.com',          smtp_port: 587, smtp_use_tls: true },
    };
    setForm((f) => ({ ...f, ...(presets[provider] || {}) }));
  };

  if (isLoading) return <Loader2 className="h-6 w-6 animate-spin mx-auto" />;

  return (
    <div className="space-y-6" data-testid="school-email-settings">
      <div>
        <h2 className="text-lg font-semibold">School Email Settings</h2>
        <p className="text-sm text-slate-500 mt-1">
          Either send through the platform&apos;s SMTP (just set the sender identity below) OR connect this school&apos;s own Gmail / Outlook / Yahoo account in the &ldquo;Use this school&apos;s own SMTP&rdquo; section.
        </p>
      </div>

      <Card>
        <CardContent className="p-4 space-y-4">
          <h3 className="font-semibold text-sm uppercase tracking-wide text-slate-600">Sender identity</h3>
          <div>
            <Label>Sender Email</Label>
            <Input value={form.sender_email} onChange={(e) => setForm({ ...form, sender_email: e.target.value })} placeholder="e.g. info@yourschool.edu" data-testid="school-sender-email" />
            <p className="text-xs text-slate-400 mt-1">Appears as the &ldquo;From&rdquo; address on every email.</p>
          </div>
          <div>
            <Label>Sender Name</Label>
            <Input value={form.sender_name} onChange={(e) => setForm({ ...form, sender_name: e.target.value })} placeholder="e.g. FWBC School" data-testid="school-sender-name" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="font-semibold text-sm uppercase tracking-wide text-slate-600">Use this school&apos;s own SMTP</h3>
              <p className="text-xs text-slate-500 mt-1">
                Turn ON to send emails directly from this school&apos;s Gmail / Outlook / Yahoo account instead of the platform&apos;s shared mailbox.
              </p>
            </div>
            <Switch
              checked={form.smtp_enabled}
              onCheckedChange={(v) => { setForm({ ...form, smtp_enabled: v }); if (v) setShowAdvanced(true); }}
              data-testid="school-smtp-enabled"
            />
          </div>

          {(showAdvanced || form.smtp_enabled) && (
            <>
              <div className="flex flex-wrap gap-2 pt-1">
                <Button type="button" size="sm" variant="outline" onClick={() => applyPreset('gmail')}   data-testid="preset-gmail">Gmail preset</Button>
                <Button type="button" size="sm" variant="outline" onClick={() => applyPreset('outlook')} data-testid="preset-outlook">Outlook preset</Button>
                <Button type="button" size="sm" variant="outline" onClick={() => applyPreset('yahoo')}   data-testid="preset-yahoo">Yahoo preset</Button>
                <Button type="button" size="sm" variant="outline" onClick={() => applyPreset('zoho')}    data-testid="preset-zoho">Zoho preset</Button>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <Label>SMTP Host</Label>
                  <Input value={form.smtp_host} onChange={(e) => setForm({ ...form, smtp_host: e.target.value })} placeholder="smtp.gmail.com" data-testid="school-smtp-host" />
                </div>
                <div>
                  <Label>SMTP Port</Label>
                  <Input type="number" value={form.smtp_port} onChange={(e) => setForm({ ...form, smtp_port: Number(e.target.value) })} placeholder="587" data-testid="school-smtp-port" />
                </div>
              </div>
              <div>
                <Label>SMTP Username (usually the email address)</Label>
                <Input value={form.smtp_user} onChange={(e) => setForm({ ...form, smtp_user: e.target.value })} placeholder="you@gmail.com" data-testid="school-smtp-user" />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  SMTP Password
                  {passwordSet && (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">Saved</span>
                  )}
                </Label>
                <Input
                  type="password"
                  value={form.smtp_password}
                  onChange={(e) => setForm({ ...form, smtp_password: e.target.value })}
                  placeholder={passwordSet ? 'Leave blank to keep the existing saved password' : 'Gmail App Password (16 chars, no spaces)'}
                  data-testid="school-smtp-password"
                  autoComplete="new-password"
                />
                <p className="text-xs text-slate-400 mt-1">
                  For Gmail: enable 2-Step Verification, then create an <strong>App Password</strong> at{' '}
                  <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="underline text-blue-600">myaccount.google.com/apppasswords</a>. Paste the 16-character password here.
                </p>
              </div>
              <div className="flex items-center justify-between bg-slate-50 border rounded p-3">
                <Label className="text-xs">Use TLS (recommended)</Label>
                <Switch checked={form.smtp_use_tls} onCheckedChange={(v) => setForm({ ...form, smtp_use_tls: v })} data-testid="school-smtp-tls" />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <div className="flex gap-2">
        <Button onClick={() => saveMutation.mutate(form)} disabled={saveMutation.isPending} data-testid="save-email-settings-btn">
          {saveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Save Settings
        </Button>
        <Button variant="outline" onClick={() => testMutation.mutate()} disabled={testMutation.isPending} data-testid="test-email-btn">
          {testMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
          Send Test Email to Me
        </Button>
      </div>
    </div>
  );
};
